import {useState} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {
  EMAIL_ALREADY_REGISTERED,
  InviteRequestApiError,
  submitInviteRequest,
} from '#/lib/api/invite-request'
import {atoms as a, platform, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import * as Toggle from '#/components/forms/Toggle'
import {Envelope_Stroke2_Corner0_Rounded as Envelope} from '#/components/icons/Envelope'
import {Globe_Stroke2_Corner0_Rounded as Globe} from '#/components/icons/Globe'
import {PencilLine_Stroke2_Corner0_Rounded as PencilLine} from '#/components/icons/Pencil'
import {Phone_Stroke2_Corner0_Rounded as Phone} from '#/components/icons/Phone'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'

const CULTURAL_IDENTITY_OPTIONS = [
  {
    value: 'black-american',
    label: 'Black American (FBA, Soulaan, American Negro)',
  },
  {value: 'indigenous-aboriginal', label: 'Indigenous / Aboriginal'},
  {value: 'afro-caribbean', label: 'Afro-Caribbean'},
  {value: 'afro-latino-negro', label: 'Afro-Latino/Negro'},
  {
    value: 'african',
    label: 'African (continental or diasporic roots)',
  },
  {value: 'mixed', label: 'Mixed'},
  {value: 'other', label: 'Other'},
]

export function InviteRequestDialog({
  control,
  initialEmail,
}: {
  control: Dialog.DialogOuterProps['control']
  initialEmail?: string
}) {
  return (
    <Dialog.Outer control={control} nativeOptions={{preventExpansion: true}}>
      <Dialog.Handle />
      <DialogInner initialEmail={initialEmail} />
    </Dialog.Outer>
  )
}

function DialogInner({initialEmail}: {initialEmail?: string}) {
  const control = Dialog.useDialogContext()
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()

  const [email, setEmail] = useState(initialEmail ?? '')
  const [culturalIdentities, setCulturalIdentities] = useState<string[]>([])
  const [phone, setPhone] = useState('')
  const [socialProfile, setSocialProfile] = useState('')
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [emailError, setEmailError] = useState(false)
  const [cultureError, setCultureError] = useState(false)

  const handleSubmit = async () => {
    let hasError = false

    if (!email.trim()) {
      setEmailError(true)
      hasError = true
    } else {
      setEmailError(false)
    }

    if (culturalIdentities.length === 0) {
      setCultureError(true)
      hasError = true
    } else {
      setCultureError(false)
    }

    if (hasError) {
      Toast.show(_(msg`Please fill in all required fields.`), {type: 'error'})
      return
    }

    setIsSubmitting(true)

    try {
      await submitInviteRequest({
        email: email.trim(),
        culturalIdentities,
        phone: phone.trim() || undefined,
        socialProfile: socialProfile.trim() || undefined,
        reason: reason.trim() || undefined,
      })

      control.close(() => {
        Toast.show(
          _(
            msg`Your invite request has been submitted! We'll be in touch soon.`,
          ),
          {type: 'success'},
        )
      })
    } catch (error) {
      if (
        error instanceof InviteRequestApiError &&
        (error.code === EMAIL_ALREADY_REGISTERED || error.status === 409)
      ) {
        control.close(() => {
          Toast.show(_(msg`This email is already on the invite list.`), {
            type: 'success',
          })
        })
        return
      }
      Toast.show(_(msg`Failed to submit request. Please try again.`), {
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog.ScrollableInner
      accessibilityDescribedBy="invite-dialog-description"
      accessibilityLabelledBy="invite-dialog-title"
      style={web({maxWidth: 500})}>
      <View style={[a.relative, a.gap_md, a.w_full]}>
        <Text nativeID="invite-dialog-title" style={[a.text_2xl, a.font_bold]}>
          <Trans>Request an Invite Code</Trans>
        </Text>

        <Text
          nativeID="invite-dialog-description"
          style={[t.atoms.text_contrast_medium, a.text_sm, a.leading_snug]}>
          <Trans>
            Fill out the form below to request an invite code.
            <br />
            We'll review your request and get back to you as soon as possible.
          </Trans>
        </Text>

        {/* Email */}
        <View>
          <View style={[a.flex_row]}>
            <TextField.LabelText>
              <Trans>Email Address</Trans>
            </TextField.LabelText>
            <Text style={[{color: t.palette.negative_500}]}> *</Text>
          </View>
          <TextField.Root isInvalid={emailError}>
            <TextField.Icon icon={Envelope} />
            <Dialog.Input
              testID="inviteEmailInput"
              value={email}
              onChangeText={value => {
                setEmail(value)
                if (emailError && value.trim()) {
                  setEmailError(false)
                }
              }}
              label={_(msg`Enter your email address`)}
              autoCapitalize="none"
              autoComplete="email"
              keyboardType="email-address"
            />
          </TextField.Root>
        </View>

        {/* Cultural Identity Multi-Select */}
        <View>
          <View style={[a.flex_row]}>
            <TextField.LabelText>
              <Trans>
                How do you culturally identify? (Select All That Apply)
              </Trans>
            </TextField.LabelText>
            <Text style={[{color: t.palette.negative_500}]}> *</Text>
          </View>
          <Toggle.Group
            label={_(msg`Cultural identity selection`)}
            type="checkbox"
            values={culturalIdentities}
            onChange={values => {
              setCulturalIdentities(values)
              if (cultureError && values.length > 0) {
                setCultureError(false)
              }
            }}>
            <View
              style={[
                a.gap_sm,
                a.pt_xs,
                a.px_sm,
                a.pb_sm,
                a.rounded_sm,
                cultureError && {
                  borderWidth: 1,
                  borderColor: t.palette.negative_500,
                },
              ]}>
              {CULTURAL_IDENTITY_OPTIONS.map(option => (
                <Toggle.Item
                  key={option.value}
                  name={option.value}
                  label={option.label}>
                  <Toggle.Checkbox />
                  <Toggle.LabelText>{option.label}</Toggle.LabelText>
                </Toggle.Item>
              ))}
            </View>
          </Toggle.Group>
          <Text
            style={[
              t.atoms.text_contrast_medium,
              a.text_xs,
              a.mt_xs,
              a.leading_snug,
            ]}>
            <Trans>
              For example: A Black American with Choctaw, Cherokee, Yamasee,
              Gullah, or Seminole, etc. ancestry can check both: Black American
              and Indigenous/Aboriginal
            </Trans>
          </Text>
        </View>

        {/* Phone (optional) */}
        <View>
          <TextField.LabelText>
            <Trans>Phone Number (optional)</Trans>
          </TextField.LabelText>
          <TextField.Root>
            <TextField.Icon icon={Phone} />
            <Dialog.Input
              testID="invitePhoneInput"
              value={phone}
              onChangeText={setPhone}
              label={_(msg`Enter your phone number`)}
              autoCapitalize="none"
              autoComplete="tel"
              keyboardType="phone-pad"
            />
          </TextField.Root>
          <Text
            style={[
              t.atoms.text_contrast_medium,
              a.text_xs,
              a.mt_xs,
              a.leading_snug,
            ]}>
            <Trans>
              For better communication if necessary but not required.
            </Trans>
          </Text>
        </View>

        {/* Social Media Profile (optional) */}
        <View>
          <TextField.LabelText>
            <Trans>Social Media Profile (optional)</Trans>
          </TextField.LabelText>
          <TextField.Root>
            <TextField.Icon icon={Globe} />
            <Dialog.Input
              testID="inviteSocialInput"
              value={socialProfile}
              onChangeText={setSocialProfile}
              label={_(msg`Link to your social media profile`)}
              autoCapitalize="none"
              keyboardType="url"
            />
          </TextField.Root>
          <Text
            style={[
              t.atoms.text_contrast_medium,
              a.text_xs,
              a.mt_xs,
              a.leading_snug,
            ]}>
            <Trans>
              Link your profile url to help us verify your identity.
            </Trans>
          </Text>
        </View>

        {/* Why do you want to join? (optional) */}
        <View>
          <TextField.LabelText>
            <Trans>Why do you want to join? (optional)</Trans>
          </TextField.LabelText>
          <TextField.Root>
            <TextField.Icon icon={PencilLine} />
            <Dialog.Input
              testID="inviteReasonInput"
              value={reason}
              onChangeText={setReason}
              label={_(msg`Tell us why you'd like to join`)}
              multiline
              numberOfLines={3}
              style={[a.py_sm]}
            />
          </TextField.Root>
        </View>

        <View
          style={[a.pt_md, a.gap_md, gtMobile && [a.flex_row, a.justify_end]]}>
          <Button
            testID="inviteCancelBtn"
            variant="solid"
            color="secondary"
            size={platform({
              native: 'large',
              web: 'small',
            })}
            onPress={() => control.close()}
            label={_(msg`Cancel`)}>
            <ButtonText>
              <Trans>Cancel</Trans>
            </ButtonText>
          </Button>
          <Button
            testID="inviteSubmitBtn"
            variant="solid"
            color="primary"
            size={platform({
              native: 'large',
              web: 'small',
            })}
            onPress={() => {
              void handleSubmit()
            }}
            disabled={isSubmitting}
            label={_(msg`Submit request`)}>
            <ButtonText>
              {isSubmitting ? (
                <Trans>Submitting...</Trans>
              ) : (
                <Trans>Submit Request</Trans>
              )}
            </ButtonText>
          </Button>
        </View>
      </View>
    </Dialog.ScrollableInner>
  )
}
